import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  Platform,
  Image,
} from 'react-native';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { SignatureCanvas, SignatureDisplay } from '../ui/SignatureCanvas';
import { AssignmentPhotoUploader } from '../ui/AssignmentPhotoUploader';
import { Vehicle, Driver } from '../../types';
import theme from '../../theme';

interface DamageMarker {
  id: string;
  x: number;
  y: number;
  type: 'scratch' | 'dent' | 'missing' | 'broken';
  severity: 'minor' | 'moderate' | 'major';
  description?: string;
  timestamp: string;
}

interface BilingualFormData {
  // Basic Information
  branch: string;
  vehicleId: string;
  licensePlate: string;
  driverName: string;
  driverPhone: string;
  
  // Partner Driver Information (for shared assignments)
  isSharedAssignment?: boolean;
  secondaryDriverName?: string;
  secondaryDriverPhone?: string;
  partnershipType?: 'shared' | 'single';
  
  // Rate Information
  monthlyRate?: number; // Monthly rental rate (primary input for partnerships)
  dailyRate?: number; // Calculated from monthly rate
  
  // Vehicle Delivery Data
  deliveryDate: string;
  deliveryTime: string;
  fuelQuantity: string;
  vehicleNumber: string; // Same as license plate
  makeModelYear: string; // Combined from vehicle table
  deliveryMileage: string;
  cleanlinessExternal: string;
  cleanlinessInternal: string;
  tireCondition: string;
  lightsCondition: string;
  signalsCondition: string;
  jackCondition: string;
  fireExtinguisherCondition: string;
  spareTireCondition: string;
  toolsCondition: string;
  vehicleShapeCondition: string;
  deliveryNotes: string;
  
  // Additional Delivery Fields
  firstAidBox: string;
  repairTools: string;
  vehicleOwnership: string;
  
  // Vehicle Receiving Data  
  receivingDate: string;
  receivingTime: string;
  receivingMileage: string;
  receivingVehicleNumber: string; // License plate for receiving vehicle (for swap)
  receivingMakeModelYear: string; // Make/model/year for receiving vehicle (for swap)
  receivingNotes: string;
  
  // Return Condition Assessment
  receivingCleanlinessExternal: string;
  receivingCleanlinessInternal: string;
  receivingTireCondition: string;
  receivingLightsCondition: string;
  receivingSignalsCondition: string;
  receivingJackCondition: string;
  receivingFireExtinguisherCondition: string;
  receivingToolsCondition: string;
  receivingSpareTireCondition: string;
  receivingVehicleShapeCondition: string;
  
  // Signatures
  driverSignature: string;
  secondaryDriverSignature: string;
  companySignature: string;
  date: string;
  
  // Reference numbers
  receiptNumber?: string;
  documentNumber?: string;
  referenceNumber?: string;
  
  // Meta data
  formType?: 'delivery' | 'receiving' | 'swap';
  submittedAt?: string;
  
  // Vehicle damage markers
  vehicleDamages?: DamageMarker[] | {
    deliveryDamages: DamageMarker[];
    receivingDamages: DamageMarker[];
  };
  
  // Photos for documentation
  deliveryPhotos?: string[];
  receivingPhotos?: string[];
}

interface BilingualAssignmentFormProps {
  formType: 'delivery' | 'receiving' | 'swap';
  vehicle?: Vehicle;
  driver?: Driver;
  secondaryDriver?: Driver; // For partner assignments
  isSharedAssignment?: boolean; // Flag for partner mode
  currentVehicle?: Vehicle; // For swap operations
  newVehicle?: Vehicle; // For swap operations
  onSubmit: (data: BilingualFormData) => void;
  onCancel: () => void;
  onPrint?: () => void; // Now optional since we handle it internally
}

export const BilingualAssignmentForm: React.FC<BilingualAssignmentFormProps> = ({
  formType,
  vehicle,
  driver,
  secondaryDriver,
  isSharedAssignment = false,
  onSubmit,
  onCancel,
  onPrint
}) => {
  const [formData, setFormData] = useState<BilingualFormData>(() => {
    console.log('🚗 Initializing form with:', { vehicle, driver, formType });
    
    // Use the main vehicle for the form context
    const contextVehicle = vehicle;
    
    const defaultData = {
      branch: 'الشارقة لحلول النقل / Sharjah Transport Solutions',
      vehicleId: contextVehicle?.vehicleCode || contextVehicle?.id || '',
      licensePlate: contextVehicle?.licensePlate || '',
      driverName: driver?.name || '',
      driverPhone: driver?.phone || '',
      
      // Partner Driver Information
      isSharedAssignment: isSharedAssignment || false,
      secondaryDriverName: secondaryDriver?.name || '',
      secondaryDriverPhone: secondaryDriver?.phone || '',
      partnershipType: (isSharedAssignment ? 'shared' : 'single') as 'shared' | 'single',
      
      // Rate Information - Default to vehicle's monthly rental rate
      monthlyRate: contextVehicle?.monthlyRentalRate || 0,
      dailyRate: contextVehicle?.monthlyRentalRate ? Math.round(contextVehicle.monthlyRentalRate / 30) : 0,
      
      // Vehicle Delivery Data (for swap: current vehicle being returned)
      deliveryDate: new Date().toISOString().split('T')[0],
      deliveryTime: new Date().toTimeString().split(' ')[0].substring(0, 5),
      fuelQuantity: '',
      vehicleNumber: contextVehicle?.licensePlate || '',
      makeModelYear: `${contextVehicle?.makeModel || ''} ${contextVehicle?.year || ''}`.trim(),
      deliveryMileage: contextVehicle?.currentMileage?.toString() || '',
      cleanlinessExternal: 'جيد / Good',
      cleanlinessInternal: 'جيد / Good',
      tireCondition: 'جيد / Good',
      lightsCondition: 'جيد / Good',
      signalsCondition: 'جيد / Good',
      jackCondition: 'جيد / Good',
      fireExtinguisherCondition: 'جيد / Good',
      spareTireCondition: 'جيد / Good',
      toolsCondition: 'جيد / Good',
      vehicleShapeCondition: 'جيد / Good',
      deliveryNotes: '',
      
      // Additional Delivery Fields
      firstAidBox: 'جيد / Good',
      repairTools: 'جيد / Good',
      vehicleOwnership: '',
      
      // Vehicle Receiving Data (for swap: new vehicle being assigned)
      receivingDate: new Date().toISOString().split('T')[0],
      receivingTime: new Date().toTimeString().split(' ')[0].substring(0, 5),
      receivingMileage: contextVehicle?.currentMileage?.toString() || '',
      receivingVehicleNumber: contextVehicle?.licensePlate || '',
      receivingMakeModelYear: `${contextVehicle?.makeModel || ''} ${contextVehicle?.year || ''}`.trim(),
      receivingNotes: '',
      
      // Return Condition Assessment
      receivingCleanlinessExternal: 'جيد / Good',
      receivingCleanlinessInternal: 'جيد / Good',
      receivingTireCondition: 'جيد / Good',
      receivingLightsCondition: 'جيد / Good',
      receivingSignalsCondition: 'جيد / Good',
      receivingJackCondition: 'جيد / Good',
      receivingFireExtinguisherCondition: 'جيد / Good',
      receivingToolsCondition: 'جيد / Good',
      receivingSpareTireCondition: 'جيد / Good',
      receivingVehicleShapeCondition: 'جيد / Good',
      
      driverSignature: '',
      secondaryDriverSignature: '',
      companySignature: '',
      date: new Date().toISOString().split('T')[0]
    };
    
    // Merge with historical data if provided
    return defaultData;
  });

  const [showDriverSignature, setShowDriverSignature] = useState(false);
  const [showSecondaryDriverSignature, setShowSecondaryDriverSignature] = useState(false);
  const [showCompanySignature, setShowCompanySignature] = useState(false);
  
  // For swap form, we need separate damage markers for each vehicle
  const [deliveryDamageMarkers, setDeliveryDamageMarkers] = useState<DamageMarker[]>([]);
  const [receivingDamageMarkers, setReceivingDamageMarkers] = useState<DamageMarker[]>([]);
  
  // For non-swap forms, use single damage markers (backward compatibility)
  const [damageMarkers, setDamageMarkers] = useState<DamageMarker[]>([]);
  
  // Photo states for delivery and receiving
  const [deliveryPhotos, setDeliveryPhotos] = useState<string[]>([]);
  const [receivingPhotos, setReceivingPhotos] = useState<string[]>([]);
  
  const [selectedDamageType, setSelectedDamageType] = useState<DamageMarker['type']>('scratch');
  const [selectedSeverity, setSelectedSeverity] = useState<DamageMarker['severity']>('minor');

  // Load historical damage markers if provided
  // Load print CSS for web platform
  useEffect(() => {
    if (Platform.OS === 'web') {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = '/src/styles/print.css';
      document.head.appendChild(link);
      
      return () => {
        document.head.removeChild(link);
      };
    }
  }, []);

  // Generate auto-reference number
  const generateReferenceNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const typeCode = formType.toUpperCase().substring(0, 3);
    const sequence = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `STSC-${year}-${typeCode}-${sequence}`;
  };

  const handlePrint = () => {
    if (Platform.OS === 'web') {
      // Generate reference number if not exists
      if (!formData.receiptNumber) {
        const refNumber = generateReferenceNumber();
        setFormData(prev => ({ ...prev, receiptNumber: refNumber }));
      }

      // Add print-specific CSS
      const printStyle = document.createElement('style');
      printStyle.id = 'print-hide-styles';
      printStyle.textContent = `
        @media print {
          /* Hide all navigation and sidebar elements */
          nav, .sidebar, .navigation, .menu, .drawer, .header-buttons,
          .back-button, .app-header, .app-navigation, .screen-header,
          .top-bar, .status-bar, .tab-bar, .bottom-navigation,
          .main-navigation, .navigation-drawer, .app-drawer,
          .react-navigation-stack, .rn-screen, .floating-button, .fab,
          .modal-backdrop, .print-hidden, [data-print="hidden"],
          .no-print, .action-buttons, .button-section,
          button:not(.print-only), .button:not(.print-only),
          [class*="button"]:not(.print-only), .footer,
          .header, [class*="header"], [id*="header"],
          [role="navigation"], [data-testid*="navigation"],
          [data-testid*="sidebar"], [data-testid*="menu"] {
            display: none !important;
            visibility: hidden !important;
            position: absolute !important;
            left: -9999px !important;
            top: -9999px !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
          }

          /* Hide the main app sidebar (both web and mobile versions) */
          div[style*="width: 280px"], div[style*="width: 240px"],
          div[style*="background-color"], div[style*="border-right"],
          div[style*="flex-direction: row"], div[style*="align-items: center"],
          div[style*="justify-content: space-between"] {
            display: none !important;
            visibility: hidden !important;
            position: absolute !important;
            left: -9999px !important;
            top: -9999px !important;
            width: 0 !important;
            height: 0 !important;
          }

          /* Hide sidebar content specifically */
          div[style*="padding:"][style*="flex-direction: row"][style*="align-items: center"] {
            display: none !important;
          }

          /* Hide navigation menu items */
          div[style*="flex-direction: row"][style*="align-items: center"][style*="padding-vertical"] {
            display: none !important;
          }

          /* Hide the STSC Fleet header in sidebar */
          div[style*="flex-direction: row"][style*="align-items: center"][style*="gap: 8"] {
            display: none !important;
          }

          /* Hide any modal overlays */
          div[style*="background-color: rgba(0,0,0,0.5)"] {
            display: none !important;
          }

          /* Show only the form container */
          #bilingual-form-container, .bilingual-form-container {
            display: block !important;
            visibility: visible !important;
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 20px !important;
            background: white !important;
            box-shadow: none !important;
            border: none !important;
          }

          /* Ensure form content is visible */
          #bilingual-form-container *, .bilingual-form-container * {
            display: block !important;
            visibility: visible !important;
            position: static !important;
          }

          /* Hide action buttons in print */
          .button-section, .action-buttons, .submitButton, .printButton, .cancelButton {
            display: none !important;
          }

          /* Hide any remaining navigation elements by their content */
          div:contains("STSC Fleet"), div:contains("Dashboard"), div:contains("Vehicles"),
          div:contains("Drivers"), div:contains("Assignments"), div:contains("Analytics") {
            display: none !important;
          }

          /* Adjust page margins for print */
          @page {
            margin: 0.5in;
            size: A4;
          }

          body {
            margin: 0 !important;
            padding: 0 !important;
          }

          /* Hide the main content wrapper that contains sidebar */
          div[style*="flex-direction: row"][style*="height: 100%"] {
            display: block !important;
          }

          /* Ensure only the form content area is visible */
          div[style*="background-color:"][style*="padding:"] {
            width: 100% !important;
            margin: 0 !important;
          }
        }
      `;
      document.head.appendChild(printStyle);

      // Add print class to body for additional control
      document.body.classList.add('printing-mode');

      // Hide elements programmatically as backup
      const hideElements = document.querySelectorAll(`
        nav, .sidebar, .navigation, .menu, .drawer, .header-buttons,
        .back-button, .app-header, .app-navigation, .screen-header,
        .top-bar, .status-bar, .tab-bar, .bottom-navigation,
        .main-navigation, .navigation-drawer, .app-drawer,
        .react-navigation-stack, .rn-screen, .floating-button, .fab,
        .modal-backdrop, .print-hidden, [data-print="hidden"],
        .no-print, .action-buttons, .button-section,
        button:not(.print-only), .button:not(.print-only),
        [class*="button"]:not(.print-only), .footer,
        .header, [class*="header"], [id*="header"],
        [role="navigation"], [data-testid*="navigation"],
        [data-testid*="sidebar"], [data-testid*="menu"]
      `);

      // Additional selectors for the app's specific sidebar structure
      const sidebarElements = document.querySelectorAll(`
        div[style*="width: 280px"], div[style*="width: 240px"],
        div[style*="background-color"][style*="border-right"],
        div[style*="flex-direction: row"][style*="align-items: center"],
        div[style*="justify-content: space-between"],
        div[style*="padding:"][style*="flex-direction: row"][style*="align-items: center"],
        div[style*="background-color: rgba(0,0,0,0.5)"]
      `);

      // Combine all elements to hide
      const allElementsToHide = [...Array.from(hideElements), ...Array.from(sidebarElements)];

      allElementsToHide.forEach(el => {
        (el as HTMLElement).style.setProperty('display', 'none', 'important');
        (el as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
        (el as HTMLElement).style.setProperty('position', 'absolute', 'important');
        (el as HTMLElement).style.setProperty('left', '-9999px', 'important');
        (el as HTMLElement).style.setProperty('top', '-9999px', 'important');
      });

      // Ensure form container is visible and properly positioned
      const formContainer = document.querySelector('#bilingual-form-container, .bilingual-form-container');
      if (formContainer) {
        (formContainer as HTMLElement).style.setProperty('display', 'block', 'important');
        (formContainer as HTMLElement).style.setProperty('visibility', 'visible', 'important');
        (formContainer as HTMLElement).style.setProperty('position', 'static', 'important');
        (formContainer as HTMLElement).style.setProperty('width', '100%', 'important');
        (formContainer as HTMLElement).style.setProperty('height', 'auto', 'important');
        (formContainer as HTMLElement).style.setProperty('margin', '0', 'important');
        (formContainer as HTMLElement).style.setProperty('padding', '20px', 'important');
        (formContainer as HTMLElement).style.setProperty('background', 'white', 'important');
      }

      // Hide action buttons
      const actionButtons = document.querySelectorAll('.button-section, .action-buttons, .submitButton, .printButton, .cancelButton');
      actionButtons.forEach(btn => {
        (btn as HTMLElement).style.setProperty('display', 'none', 'important');
        (btn as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
      });

      // Trigger print with delay to ensure styles are applied
      setTimeout(() => {
        window.print();

        // Cleanup after print
        setTimeout(() => {
          document.body.classList.remove('printing-mode');

          // Remove print styles
          const printStyleElement = document.getElementById('print-hide-styles');
          if (printStyleElement) {
            document.head.removeChild(printStyleElement);
          }

          // Restore hidden elements
          hideElements.forEach(el => {
            (el as HTMLElement).style.removeProperty('display');
            (el as HTMLElement).style.removeProperty('visibility');
            (el as HTMLElement).style.removeProperty('position');
            (el as HTMLElement).style.removeProperty('left');
            (el as HTMLElement).style.removeProperty('top');
          });

          // Restore sidebar elements
          sidebarElements.forEach(el => {
            (el as HTMLElement).style.removeProperty('display');
            (el as HTMLElement).style.removeProperty('visibility');
            (el as HTMLElement).style.removeProperty('position');
            (el as HTMLElement).style.removeProperty('left');
            (el as HTMLElement).style.removeProperty('top');
          });

          if (formContainer) {
            (formContainer as HTMLElement).style.removeProperty('display');
            (formContainer as HTMLElement).style.removeProperty('visibility');
            (formContainer as HTMLElement).style.removeProperty('position');
            (formContainer as HTMLElement).style.removeProperty('width');
            (formContainer as HTMLElement).style.removeProperty('height');
            (formContainer as HTMLElement).style.removeProperty('margin');
            (formContainer as HTMLElement).style.removeProperty('padding');
            (formContainer as HTMLElement).style.removeProperty('background');
          }

          actionButtons.forEach(btn => {
            (btn as HTMLElement).style.removeProperty('display');
            (btn as HTMLElement).style.removeProperty('visibility');
          });
        }, 1000);
      }, 300);
    } else {
      Alert.alert('طباعة / Print', 'الطباعة متاحة على المتصفح فقط / Printing available on web only');
    }
  };

  const conditionOptions = [
    'ممتاز / Excellent',
    'جيد / Good', 
    'متوسط / Fair',
    'سيء / Poor'
  ];

  const handleInputChange = (
    field: keyof BilingualFormData,
    value: string | number | boolean | DamageMarker[] | BilingualFormData['vehicleDamages']
  ) => {
    setFormData(prev => ({ ...prev, [field]: value as any }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submittedReferenceNumber, setSubmittedReferenceNumber] = useState<string>('');

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting || isSubmitted) {
      console.log('🚗 Submission already in progress or completed, ignoring click');
      return;
    }

    console.log('🚗 BilingualAssignmentForm: Submit button pressed');
    console.log('🚗 Current form data:', formData);
    console.log('🚗 Form type:', formType);
    console.log('🚗 Vehicle:', vehicle);
    console.log('🚗 Driver:', driver);

    // Basic validation
    if (!formData.licensePlate || !formData.driverName) {
      console.log('🚗 Validation failed: Missing licensePlate or driverName');
      Alert.alert(
        'خطأ في التحقق / Validation Error',
        'يرجى ملء جميع الحقول المطلوبة:\n- رقم اللوحة (License Plate)\n- اسم السائق (Driver Name)\n\nPlease fill all required fields:\n- License Plate\n- Driver Name',
        [{ text: 'موافق / OK' }]
      );
      return;
    }

    setIsSubmitting(true);

    // Generate reference number
    const referenceNumber = generateReferenceNumber();
    setSubmittedReferenceNumber(referenceNumber); // Save for success screen
    const submissionData = {
      ...formData,
      receiptNumber: formData.receiptNumber || referenceNumber,
      documentNumber: formData.documentNumber || referenceNumber,
      referenceNumber,
      formType,
      vehicleId: vehicle?.id || '',
      driverId: driver?.id || '',
      secondaryDriverId: secondaryDriver?.id || null, // Add secondary driver ID
      isSharedAssignment: isSharedAssignment, // Add shared assignment flag
      vehicleDamages: damageMarkers,
      deliveryPhotos: deliveryPhotos, // Add delivery photos
      receivingPhotos: receivingPhotos, // Add receiving photos
      submittedAt: new Date().toISOString()
    };

    console.log('🚗 BilingualAssignmentForm: Submitting form data:', submissionData);
    console.log('🚗 Calling onSubmit with data...');
    
    let errorOccurred = false;
    
    try {
      console.log('🚗 About to call onSubmit...');
      await onSubmit(submissionData);
      console.log('🚗 onSubmit called successfully');
      
      console.log('🚗 Setting isSubmitted to true...');
      setIsSubmitted(true); // Mark as submitted
      console.log('🚗 isSubmitted set to true');
      
      console.log('🚗 Showing success screen...');
      // Show success screen instead of popup
      setShowSuccessScreen(true);
      console.log('🚗 Success screen shown');
    } catch (error: any) {
      console.error('🚗 Error calling onSubmit:', error);
      errorOccurred = true;
      setIsSubmitting(false); // Reset if error occurred
      
      // Show error alert instead of success
      Alert.alert(
        'خطأ في التحقق / Validation Error',
        `فشل في إرسال النموذج / Form submission failed:\n\n${error.message}`,
        [{ text: 'موافق / OK' }]
      );
      
      // Re-throw the error so the parent component can handle it properly
      throw error;
    } finally {
      console.log('🚗 Finally block - resetting isSubmitting but preserving isSubmitted state');
      // Only reset isSubmitting if no error occurred
      if (!errorOccurred) {
        setIsSubmitting(false);
      }
    }
  };

  // Handle adding damage markers - context-aware for swap vs regular forms
  const handleImagePress = (event: any, section?: 'delivery' | 'receiving') => {
    let x, y;
    
    if (Platform.OS === 'web') {
      // For web, we need to calculate relative coordinates
      const rect = event.currentTarget.getBoundingClientRect();
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
    } else {
      // For mobile, use locationX and locationY
      const { locationX, locationY } = event.nativeEvent;
      x = locationX;
      y = locationY;
    }

    const newMarker: DamageMarker = {
      id: `damage_${Date.now()}`,
      x: x,
      y: y,
      type: selectedDamageType,
      severity: selectedSeverity,
      timestamp: new Date().toISOString()
    };
    
    // Add to appropriate state based on context
    if (formType === 'swap' && section) {
      if (section === 'delivery') {
        setDeliveryDamageMarkers(prev => [...prev, newMarker]);
      } else {
        setReceivingDamageMarkers(prev => [...prev, newMarker]);
      }
    } else {
      setDamageMarkers(prev => [...prev, newMarker]);
    }
  };

  // Remove damage marker - context-aware for swap vs regular forms
  const removeDamageMarker = (markerId: string, section?: 'delivery' | 'receiving') => {
    if (formType === 'swap' && section) {
      if (section === 'delivery') {
        setDeliveryDamageMarkers(prev => prev.filter(marker => marker.id !== markerId));
      } else {
        setReceivingDamageMarkers(prev => prev.filter(marker => marker.id !== markerId));
      }
    } else {
      setDamageMarkers(prev => prev.filter(marker => marker.id !== markerId));
    }
  };

  // Get marker color based on severity
  const getMarkerColor = (severity: DamageMarker['severity']) => {
    switch (severity) {
      case 'major': return '#ef4444'; // red
      case 'moderate': return '#f59e0b'; // amber
      case 'minor': return '#10b981'; // green
      default: return '#6b7280'; // gray
    }
  };

  const renderVehicleDiagram = (section?: 'delivery' | 'receiving') => {
    // Get the appropriate damage markers based on section
    const currentMarkers = formType === 'swap' && section 
      ? (section === 'delivery' ? deliveryDamageMarkers : receivingDamageMarkers)
      : damageMarkers;

    return (
    <View style={styles.diagramContainer}>
      <Text style={styles.diagramTitle}>
        {formType === 'swap' 
          ? `مخطط المركبة ${section === 'delivery' ? 'الحالية' : 'الجديدة'} / ${section === 'delivery' ? 'Current' : 'New'} Vehicle Diagram`
          : 'مخطط المركبة / Vehicle Diagram'
        }
      </Text>
      
      <View style={styles.damageControls}>
        <Text style={styles.controlLabel}>نوع الضرر / Damage Type:</Text>
        <View style={styles.damageTypeRow}>
          {[
            { type: 'scratch', label: 'خدش / Scratch' },
            { type: 'dent', label: 'انبعاج / Dent' },
            { type: 'missing', label: 'مفقود / Missing' },
            { type: 'broken', label: 'كسر / Broken' }
          ].map(({ type, label }) => (
            <TouchableOpacity
              key={type}
              style={[
                styles.damageTypeButton,
                selectedDamageType === type && styles.selectedDamageType
              ]}
              onPress={() => setSelectedDamageType(type as DamageMarker['type'])}
            >
              <Text style={[
                styles.damageTypeText,
                selectedDamageType === type && styles.selectedDamageTypeText
              ]}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={styles.controlLabel}>الخطورة / Severity:</Text>
        <View style={styles.severityRow}>
          {[
            { severity: 'minor', label: 'بسيط / Minor', color: '#10b981' },
            { severity: 'moderate', label: 'متوسط / Moderate', color: '#f59e0b' },
            { severity: 'major', label: 'خطير / Major', color: '#ef4444' }
          ].map(({ severity, label, color }) => (
            <TouchableOpacity
              key={severity}
              style={[
                styles.severityButton,
                selectedSeverity === severity && { borderColor: color, backgroundColor: color + '20' }
              ]}
              onPress={() => setSelectedSeverity(severity as DamageMarker['severity'])}
            >
              <Text style={[
                styles.severityText,
                selectedSeverity === severity && { color: color }
              ]}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Interactive Vehicle Diagram */}
      <View style={styles.vehicleDiagram}>
        <View style={styles.imageContainer}>
          <TouchableOpacity
            onPress={(event) => handleImagePress(event, section)}
            activeOpacity={0.8}
            style={styles.touchableImage}
          >
            <Image
              source={require('../../assets/tesla-model-s.jpeg')}
              style={styles.teslaImage}
              resizeMode="contain"
            />
            
            {/* SVG Overlay for Damage Markers */}
            <Svg style={styles.svgOverlay} pointerEvents="none">
              {currentMarkers.map((marker, index) => (
                <React.Fragment key={marker.id}>
                  <Circle
                    cx={marker.x}
                    cy={marker.y}
                    r="8"
                    fill={getMarkerColor(marker.severity)}
                    stroke="#ffffff"
                    strokeWidth="2"
                    onPress={() => removeDamageMarker(marker.id, section)}
                  />
                  <SvgText
                    x={marker.x}
                    y={marker.y + 2}
                    fontSize="10"
                    fill="#ffffff"
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    {index + 1}
                  </SvgText>
                </React.Fragment>
              ))}
            </Svg>
          </TouchableOpacity>
        </View>

        {/* Instructions */}
        <Text style={styles.instructionText}>
          اضغط على الصورة لإضافة ضرر • اضغط على العلامة لحذفها{'\n'}
          Tap image to add damage • Tap marker to remove
        </Text>
      </View>

      {/* Damage List */}
      {currentMarkers.length > 0 && (
        <View style={styles.damageList}>
          <Text style={styles.damageListTitle}>قائمة الأضرار / Damage List:</Text>
          {currentMarkers.map((marker, index) => (
            <View key={marker.id} style={styles.damageItem}>
              <View style={styles.damageItemHeader}>
                <View style={[styles.damageNumber, { backgroundColor: getMarkerColor(marker.severity) }]}>
                  <Text style={styles.damageNumberText}>{index + 1}</Text>
                </View>
                <View style={styles.damageInfo}>
                  <Text style={styles.damageItemText}>
                    {marker.type} - {marker.severity}
                  </Text>
                  {marker.description && (
                    <Text style={styles.damageDescription}>
                      {marker.description}
                    </Text>
                  )}
                </View>
                <TouchableOpacity
                  onPress={() => removeDamageMarker(marker.id, section)}
                  style={styles.removeButton}
                >
                  <Text style={styles.removeButtonText}>حذف / Remove</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
    );
  };

  const renderConditionSelect = (field: keyof BilingualFormData, label: string) => (
    <View style={styles.conditionItem}>
      <Text style={styles.conditionLabel}>{label}:</Text>
      <View style={styles.conditionOptions}>
        {conditionOptions.map((option) => (
          <TouchableOpacity
            key={option}
            style={[
              styles.conditionOption,
              formData[field] === option && styles.selectedCondition
            ]}
            onPress={() => handleInputChange(field, option)}
          >
            <Text style={[
              styles.conditionOptionText,
              formData[field] === option && styles.selectedConditionText
            ]}>
              {option.split(' / ')[0]}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const shouldShowDeliverySection = formType === 'delivery' || formType === 'swap';
  const shouldShowReceivingSection = formType === 'receiving' || formType === 'swap';

  const getFormTypeLabel = () => {
    switch (formType) {
      case 'delivery':
        return 'تسليم المركبة / Vehicle Delivery';
      case 'receiving':
        return 'استلام المركبة / Vehicle Receiving';
      case 'swap':
        return 'تبديل المركبة / Vehicle Swap';
      default:
        return 'نموذج المركبة / Vehicle Form';
    }
  };

  return (
    <View style={styles.container} nativeID="bilingual-form-container">
      {/* Success Screen */}
      {showSuccessScreen ? (
        <View style={styles.successScreen}>
          <View style={styles.successContent}>
            {/* Success Icon */}
            <View style={styles.successIconContainer}>
              <View style={styles.successIcon}>
                <Text style={styles.successIconText}>✓</Text>
              </View>
            </View>
            
            {/* Success Message */}
            <Text style={styles.successTitle}>تم التسليم بنجاح!</Text>
            <Text style={styles.successTitleEn}>Form Submitted Successfully!</Text>
            
            <Text style={styles.successMessage}>
              تم تسليم النموذج بنجاح وحفظه في النظام
            </Text>
            <Text style={styles.successMessageEn}>
              The form has been successfully submitted and saved to the system
            </Text>
            
            {/* Form Details */}
            <View style={styles.successDetails}>
              <Text style={styles.successDetailText}>رقم المرجع / Reference: {submittedReferenceNumber}</Text>
              <Text style={styles.successDetailText}>المركبة / Vehicle: {formData.licensePlate}</Text>
              <Text style={styles.successDetailText}>السائق / Driver: {formData.driverName}</Text>
              <Text style={styles.successDetailText}>التاريخ / Date: {formData.date}</Text>
            </View>
            
            {/* Action Buttons */}
            <View style={styles.successButtons}>
              <Button
                title="العودة للقائمة / Back to List"
                onPress={() => {
                  if (onCancel) {
                    onCancel();
                  }
                }}
                variant="primary"
                style={styles.successButton}
              />
              
              <Button
                title="طباعة / Print"
                onPress={handlePrint}
                variant="outline"
                style={styles.successButton}
              />
            </View>
          </View>
        </View>
      ) : (
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header with Logo */}
        <View style={styles.headerSection} nativeID="print-header-section">
          <View style={styles.headerContent} nativeID="print-header-content">
            <View style={styles.headerLeft} nativeID="print-header-left">
              <View style={styles.logoContainer} nativeID="print-logo-container">
                <Image
                  source={require('../../../assets/logo-icon-only.svg')}
                  style={styles.logoImage}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.companyInfo} nativeID="print-company-info">
                <Text style={styles.companyNameAr}>الشارقة لحلول النقل</Text>
                <Text style={styles.companyNameEn}>Sharjah Transport Solutions</Text>
              </View>
            </View>
            <View style={styles.headerCenter} nativeID="print-header-center">
              <Text style={styles.typeAr}>{getFormTypeLabel().split(' / ')[0]}</Text>
              <Text style={styles.typeEn}>{getFormTypeLabel().split(' / ')[1]}</Text>
            </View>
            <View style={styles.headerRight} nativeID="print-header-right">
            </View>
          </View>
        </View>

        {/* Basic Information */}
  <Card style={styles.section} nativeID="print-basic-info-card">
          <View style={styles.basicInfoGrid}>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>اسم الشركة / Company Name</Text>
              <TextInput
                style={styles.input}
                value={formData.branch}
                onChangeText={(value) => handleInputChange('branch', value)}
                placeholder="اسم الشركة"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>رقم اللوحة / License Plate</Text>
              <TextInput
                style={styles.input}
                value={formData.licensePlate}
                onChangeText={(value) => handleInputChange('licensePlate', value)}
                placeholder="رقم اللوحة"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>اسم السائق الأساسي / Primary Driver Name</Text>
              <TextInput
                style={styles.input}
                value={formData.driverName}
                onChangeText={(value) => handleInputChange('driverName', value)}
                placeholder="اسم السائق الأساسي"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>رقم الهاتف / Phone Number</Text>
              <TextInput
                style={styles.input}
                value={formData.driverPhone}
                onChangeText={(value) => handleInputChange('driverPhone', value)}
                placeholder="رقم الهاتف"
              />
            </View>
          </View>
        </Card>

        {/* Partner Driver Information (if shared assignment) */}
        {formData.isSharedAssignment && (
          <Card style={styles.section} nativeID="print-partner-card">
            <View style={styles.partnerDriverHeader}>
              <View style={styles.partnerIndicator}>
                <Ionicons name="people" size={20} color={theme.colors.primary} />
                <Text style={styles.partnerIndicatorText}>شراكة السائقين / Driver Partnership</Text>
              </View>
            </View>
            <View style={styles.basicInfoGrid}>
              <View style={styles.inputGroup}>
                <Text style={styles.labelText}>اسم السائق الثانوي / Secondary Driver Name</Text>
                <TextInput
                  style={styles.input}
                  value={formData.secondaryDriverName}
                  onChangeText={(value) => handleInputChange('secondaryDriverName', value)}
                  placeholder="اسم السائق الشريك"
                />
              </View>
              <View style={styles.inputGroup}>
                <Text style={styles.labelText}>رقم هاتف السائق الثانوي / Secondary Driver Phone</Text>
                <TextInput
                  style={styles.input}
                  value={formData.secondaryDriverPhone}
                  onChangeText={(value) => handleInputChange('secondaryDriverPhone', value)}
                  placeholder="رقم هاتف الشريك"
                />
              </View>
            </View>
          </Card>
        )}

        {/* Vehicle Delivery Data */}
        {shouldShowDeliverySection && (
          <View nativeID="print-delivery-section">
            <Card style={styles.section} nativeID="print-delivery-card">
            <View style={styles.sectionHeader} nativeID="print-delivery-header">
              <Text style={styles.sectionTitle}>
                بيانات تسليم المركبة / Vehicle Delivery Data
              </Text>
            </View>
            
            <View style={styles.deliveryContent} nativeID="print-delivery-content">
              <View style={styles.leftColumn} nativeID="print-delivery-media">
                {renderVehicleDiagram('delivery')}
                <View style={styles.notesContainer} nativeID="print-delivery-notes">
                  <Text style={styles.notesLabel}>
                    ملاحظات عامة عن حالة المركبة:{'\n'}
                    General Notes on Vehicle Condition:
                  </Text>
                  <TextInput
                    style={styles.notesInput}
                    value={formData.deliveryNotes}
                    onChangeText={(value) => handleInputChange('deliveryNotes', value)}
                    placeholder="اكتب ملاحظاتك هنا..."
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                  />
                </View>
              </View>
              
              <View style={styles.rightColumn} nativeID="print-delivery-details">
                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>تاريخ التسليم / Delivery Date</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.deliveryDate}
                      onChangeText={(value) => handleInputChange('deliveryDate', value)}
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>وقت التسليم / Delivery Time</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.deliveryTime}
                      onChangeText={(value) => handleInputChange('deliveryTime', value)}
                    />
                  </View>
                </View>

                {/* Rental Rate Fields */}
                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>الحصة الشهرية / Monthly Company Share (AED)</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.monthlyRate ? formData.monthlyRate.toString() : ''}
                      onChangeText={(value) => {
                        const numValue = parseFloat(value) || 0;
                        handleInputChange('monthlyRate', numValue);
                        // Auto-calculate daily rate (monthly / 30)
                        if (numValue > 0) {
                          handleInputChange('dailyRate', Math.round((numValue / 30) * 100) / 100);
                        }
                      }}
                      keyboardType="numeric"
                      placeholder="4500"
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>السعر اليومي / Daily Rate (AED)</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.dailyRate ? formData.dailyRate.toString() : ''}
                      onChangeText={(value) => handleInputChange('dailyRate', parseFloat(value) || 0)}
                      keyboardType="numeric"
                      placeholder="150"
                    />
                  </View>
                </View>

                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>كمية الوقود / Fuel Quantity</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.fuelQuantity}
                      onChangeText={(value) => handleInputChange('fuelQuantity', value)}
                      placeholder="كمية الوقود"
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>رقم المركبة / Vehicle Number</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.vehicleNumber}
                      onChangeText={(value) => handleInputChange('vehicleNumber', value)}
                      placeholder="رقم المركبة"
                    />
                  </View>
                </View>

                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>الماركة والموديل والسنة / Make and Model and Year</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.receivingMakeModelYear}
                      onChangeText={(value) => handleInputChange('receivingMakeModelYear', value)}
                      placeholder="الماركة والموديل والسنة"
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>قراءة عداد المسافة (كم) / Mileage Reading (km)</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.deliveryMileage}
                      onChangeText={(value) => handleInputChange('deliveryMileage', value)}
                      placeholder="قراءة العداد"
                    />
                  </View>
                </View>
                
                <View style={styles.conditionsContainer}>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('cleanlinessExternal', 'النظافة الخارجية / External Cleanliness')}
                    {renderConditionSelect('cleanlinessInternal', 'النظافة الداخلية / Internal Cleanliness')}
                  </View>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('tireCondition', 'الإطارات / Tires')}
                    {renderConditionSelect('lightsCondition', 'الأضواء والإشارات / Lights & Signals')}
                  </View>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('jackCondition', 'الرافعة / Jack')}
                    {renderConditionSelect('fireExtinguisherCondition', 'طفاية الحريق / Fire Extinguisher')}
                  </View>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('spareTireCondition', 'الإطار الاحتياطي / Spare Tire')}
                    {renderConditionSelect('toolsCondition', 'الأدوات / Tools')}
                  </View>
                </View>
                
                {/* Photo Upload Section for Delivery */}
                <View style={styles.photoSection} nativeID="print-delivery-photos">
                  <AssignmentPhotoUploader
                    photos={deliveryPhotos}
                    onPhotosChange={setDeliveryPhotos}
                    maxPhotos={12}
                    formType="delivery"
                  />
                </View>
              </View>
            </View>
            </Card>
          </View>
        )}

        {/* Vehicle Receiving Data */}
        {shouldShowReceivingSection && (
          <View nativeID="print-receiving-section">
            <Card style={styles.section} nativeID="print-receiving-card">
            <View style={styles.sectionHeader} nativeID="print-receiving-header">
              <Text style={styles.sectionTitle}>
                بيانات استلام المركبة / Vehicle Receiving Data
              </Text>
            </View>
            
            <View style={styles.receivingContent} nativeID="print-receiving-content">
              <View style={styles.leftColumn} nativeID="print-receiving-media">
                {renderVehicleDiagram('receiving')}
                <View style={styles.notesContainer} nativeID="print-receiving-notes">
                  <Text style={styles.notesLabel}>
                    ملاحظات عامة عن حالة المركبة:{'\n'}
                    General Notes on Vehicle Condition:
                  </Text>
                  <TextInput
                    style={styles.notesInput}
                    value={formData.receivingNotes}
                    onChangeText={(value) => handleInputChange('receivingNotes', value)}
                    placeholder="اكتب ملاحظاتك هنا..."
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                  />
                </View>
              </View>
              
              <View style={styles.rightColumn} nativeID="print-receiving-details">
                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>تاريخ الاستلام / Receiving Date</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.receivingDate}
                      onChangeText={(value) => handleInputChange('receivingDate', value)}
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>وقت الاستلام / Receiving Time</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.receivingTime}
                      onChangeText={(value) => handleInputChange('receivingTime', value)}
                    />
                  </View>
                </View>

                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>كمية الوقود / Fuel Quantity</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.fuelQuantity}
                      onChangeText={(value) => handleInputChange('fuelQuantity', value)}
                      placeholder="كمية الوقود"
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>رقم المركبة / Vehicle Number</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.receivingVehicleNumber}
                      onChangeText={(value) => handleInputChange('receivingVehicleNumber', value)}
                      placeholder="رقم المركبة"
                    />
                  </View>
                </View>

                <View style={styles.dateTimeRow}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>الماركة والموديل والسنة / Make and Model and Year</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.receivingMakeModelYear}
                      onChangeText={(value) => handleInputChange('receivingMakeModelYear', value)}
                      placeholder="الماركة والموديل والسنة"
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>قراءة عداد المسافة (كم) / Mileage Reading (km)</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.receivingMileage}
                      onChangeText={(value) => handleInputChange('receivingMileage', value)}
                      placeholder="قراءة العداد"
                    />
                  </View>
                </View>
                
                <View style={styles.conditionsContainer}>
                  <Text style={styles.conditionsTitle}>تقييم حالة المركبة عند الإرجاع / Return Condition Assessment</Text>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('receivingCleanlinessExternal', 'النظافة الخارجية / Exterior Cleanliness')}
                    {renderConditionSelect('receivingCleanlinessInternal', 'النظافة الداخلية / Interior Cleanliness')}
                  </View>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('receivingTireCondition', 'الإطارات / Tires')}
                    {renderConditionSelect('receivingLightsCondition', 'مصابيح الإضاءة والإشارات / Lights & Signals')}
                  </View>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('receivingJackCondition', 'الرافعة / Jack')}
                    {renderConditionSelect('receivingFireExtinguisherCondition', 'طفاية الحريق / Fire Extinguisher')}
                  </View>
                  <View style={styles.conditionRow}>
                    {renderConditionSelect('receivingSpareTireCondition', 'الإطار الاحتياطي / Spare Tire')}
                    {renderConditionSelect('receivingToolsCondition', 'الأدوات / Tools')}
                  </View>
                </View>
                
                {/* Photo Upload Section for Receiving */}
                <View style={styles.photoSection} nativeID="print-receiving-photos">
                  <AssignmentPhotoUploader
                    photos={receivingPhotos}
                    onPhotosChange={setReceivingPhotos}
                    maxPhotos={12}
                    formType="receiving"
                  />
                </View>
              </View>
            </View>
            </Card>
          </View>
        )}

        {/* Driver's Pledge */}
        <View nativeID="print-pledge-section">
  <Card style={styles.section} nativeID="print-pledge-card">
          <View style={styles.pledgeSection}>
            <Text style={styles.pledgeTitle}>تعهد السائق / Driver's Pledge</Text>
            <View style={styles.pledgeContent}>
              <Text style={styles.pledgeText}>
                بإعادة المركبة بالحالة التي استلمها وأي أضرار تحدث للمركبة يتحملها السائق
              </Text>
              <Text style={styles.pledgeTextEn}>
                To return the vehicle in the condition received and bear responsibility for any damages
              </Text>
              <Text style={styles.pledgeText}>
                إبلاغ الشركة عن اقتراب موعد الصيانة أو في حالة وجود أي عطل فني بأسرع وقت
              </Text>
              <Text style={styles.pledgeTextEn}>
                Notify the company of approaching maintenance or any technical issues immediately
              </Text>
              <Text style={styles.pledgeText}>
                يتحمل السائق تكلفة الوقود ورسوم البوابة التعريفية "ساليك"
              </Text>
              <Text style={styles.pledgeTextEn}>
                Driver bears fuel costs and Salik toll fees
              </Text>
              <Text style={styles.pledgeText}>
                يتحمل السائق أي مخالفات مرورية على السيارة وهو المسؤول عن السيارة مسؤولية قانونية
              </Text>
              <Text style={styles.pledgeTextEn}>
                Driver bears legal responsibility for traffic violations and vehicle liability
              </Text>
            </View>
          </View>
        </Card>
        </View>

        {/* Signature Section */}
        <View style={styles.signatureSection} nativeID="print-signature-section">
          <View style={styles.signatureRow} nativeID="print-signature-row">
            <View style={styles.signatureWrapper} nativeID="print-signature-company">
            <Card style={styles.signatureCard} nativeID="print-company-signature-card">
              <View style={styles.signatureHeader}>
                <Text style={styles.signatureTitle}>
                  الاعتماد (شركة شارقة لحلول النقل){'\n'}
                  Approval (Sharjah Transport Solutions Company)
                </Text>
              </View>
              <View style={styles.signatureContent}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>الاسم / Name:</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="اسم مندوب الشركة"
                  />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>التاريخ / Date:</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.date}
                    onChangeText={(value) => handleInputChange('date', value)}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>التوقيع / Signature:</Text>
                  <SignatureDisplay
                    signature={formData.companySignature}
                    onEdit={() => setShowCompanySignature(true)}
                    placeholder="اضغط للتوقيع / Tap to Sign"
                    readOnly={false}
                  />
                </View>
              </View>
            </Card>
            </View>

            <View style={styles.signatureWrapper} nativeID="print-signature-driver">
            <Card style={styles.signatureCard} nativeID="print-driver-signature-card">
              <View style={styles.signatureHeader}>
                <Text style={styles.signatureTitle}>
                  الاعتماد (السائق){'\n'}
                  Approval (Driver)
                </Text>
              </View>
              <View style={styles.signatureContent}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>الاسم / Name:</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.driverName}
                    onChangeText={(value) => handleInputChange('driverName', value)}
                    placeholder="اسم السائق"
                  />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>التاريخ / Date:</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.date}
                    onChangeText={(value) => handleInputChange('date', value)}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>التوقيع / Signature:</Text>
                  <SignatureDisplay
                    signature={formData.driverSignature}
                    onEdit={() => setShowDriverSignature(true)}
                    placeholder="اضغط للتوقيع / Tap to Sign"
                    readOnly={false}
                  />
                </View>
              </View>
            </Card>
            </View>

            {/* Secondary Driver Signature - Only for shared assignments */}
            {isSharedAssignment && secondaryDriver && (
              <View style={styles.signatureWrapper} nativeID="print-signature-secondary">
              <Card style={styles.signatureCard} nativeID="print-secondary-signature-card">
                <View style={styles.signatureHeader}>
                  <Text style={styles.signatureTitle}>
                    الاعتماد (السائق الثانوي){'\n'}
                    Approval (Secondary Driver)
                  </Text>
                </View>
                <View style={styles.signatureContent}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>الاسم / Name:</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.secondaryDriverName}
                      onChangeText={(value) => handleInputChange('secondaryDriverName', value)}
                      placeholder="اسم السائق الثانوي"
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>التاريخ / Date:</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.date}
                      onChangeText={(value) => handleInputChange('date', value)}
                    />
                  </View>
                  <View style={styles.inputGroup}>
                    <Text style={styles.labelText}>التوقيع / Signature:</Text>
                    <SignatureDisplay
                      signature={formData.secondaryDriverSignature}
                      onEdit={() => setShowSecondaryDriverSignature(true)}
                      placeholder="اضغط للتوقيع / Tap to Sign"
                      readOnly={false}
                    />
                  </View>
                </View>
              </Card>
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
  <View style={styles.buttonSection} nativeID="print-action-buttons">
          <Button
            title={isSubmitting ? "جاري التسليم... / Submitting..." : 
                   isSubmitted ? "تم التسليم ✓ / Submitted ✓" :
                   "تسليم النموذج / Submit Form"}
            onPress={handleSubmit}
            variant="primary"
            style={isSubmitting || isSubmitted ? 
              { ...styles.submitButton, ...styles.submitButtonDisabled } :
              styles.submitButton}
            disabled={isSubmitting || isSubmitted}
          />
          <Button
            title="طباعة / Print"
            onPress={handlePrint}
              variant="outline"
              style={styles.printButton}
            />
            <Button
              title="إلغاء / Cancel"
              onPress={onCancel}
              variant="outline"
              style={styles.cancelButton}
            />
          </View>
        </ScrollView>
      )}

      {/* Signature Modals */}
      <>
        <SignatureCanvas
          visible={showDriverSignature}
          onClose={() => setShowDriverSignature(false)}
          onSave={(signature) => handleInputChange('driverSignature', signature)}
          title="توقيع السائق / Driver's Signature"
          placeholder="وقع هنا / Sign here"
        />

        <SignatureCanvas
            visible={showCompanySignature}
            onClose={() => setShowCompanySignature(false)}
            onSave={(signature) => handleInputChange('companySignature', signature)}
            title="توقيع الشركة / Company Signature"
            placeholder="وقع هنا / Sign here"
          />

        <SignatureCanvas
          visible={showSecondaryDriverSignature}
          onClose={() => setShowSecondaryDriverSignature(false)}
          onSave={(signature) => handleInputChange('secondaryDriverSignature', signature)}
          title="توقيع السائق الثانوي / Secondary Driver's Signature"
          placeholder="وقع هنا / Sign here"
        />
        </>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  headerSection: {
    backgroundColor: theme.colors.white,
    borderWidth: 2,
    borderColor: theme.colors.gray400,
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoContainer: {
    width: 60,
    height: 60,
    backgroundColor: theme.colors.white,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
    borderWidth: 2,
    borderColor: theme.colors.primary,
    shadowColor: theme.colors.gray900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  logoImage: {
    width: 40,
    height: 40,
  },
  companyInfo: {
    flex: 1,
  },
  companyNameAr: {
    fontSize: 16,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    textAlign: 'left',
  },
  companyNameEn: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'left',
    marginTop: 2,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  typeAr: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.primary,
    textAlign: 'center',
  },
  typeEn: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: 4,
  },
  section: {
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  basicInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  inputGroup: {
    flex: 1,
    minWidth: 200,
  },
  labelText: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
    textAlign: 'right',
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: theme.spacing.sm,
    fontSize: 14,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
    textAlign: 'right',
  },
  sectionHeader: {
    backgroundColor: theme.colors.gray100,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  deliveryContent: {
    flexDirection: 'row',
    gap: theme.spacing.lg,
  },
  receivingContent: {
    flexDirection: 'row',
    gap: theme.spacing.lg,
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  diagramContainer: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.sm,
    borderRadius: 8,
    marginBottom: theme.spacing.sm,
  },
  diagramTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  vehicleDiagram: {
    alignItems: 'center',
  },
  damageControls: {
    marginBottom: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  controlLabel: {
    fontSize: 12,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
    textAlign: 'right',
  },
  damageTypeRow: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
    flexWrap: 'wrap',
  },
  damageTypeButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 4,
    backgroundColor: theme.colors.white,
  },
  selectedDamageType: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  damageTypeText: {
    fontSize: 10,
    color: theme.colors.textPrimary,
  },
  selectedDamageTypeText: {
    color: theme.colors.white,
  },
  severityRow: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  },
  severityButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 4,
    backgroundColor: theme.colors.white,
    flex: 1,
  },
  severityText: {
    fontSize: 10,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  imageContainer: {
    position: 'relative',
    alignItems: 'center',
  },
  touchableImage: {
    position: 'relative',
  },
  teslaImage: {
    width: 350,
    height: 250,
    marginVertical: theme.spacing.xs,
  },
  svgOverlay: {
    position: 'absolute',
    top: theme.spacing.xs,
    left: 0,
    width: 350,
    height: 250,
    zIndex: 1,
  },
  instructionText: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
    fontStyle: 'italic' as any,
  },
  damageList: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  damageListTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  damageItem: {
    marginBottom: theme.spacing.xs,
  },
  damageItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  damageNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  damageNumberText: {
    color: theme.colors.white,
    fontSize: 12,
    fontWeight: 'bold' as any,
  },
  damageItemText: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
  },
  damageInfo: {
    flex: 1,
    marginHorizontal: theme.spacing.sm,
  },
  damageDescription: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    marginTop: 2,
    fontStyle: 'italic' as any,
  },
  removeButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.colors.error,
    borderRadius: 4,
  },
  removeButtonText: {
    color: theme.colors.white,
    fontSize: 10,
  },
  sideLabel: {
    fontSize: 10,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  notesContainer: {
    marginTop: theme.spacing.md,
  },
  notesLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  notesInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: theme.spacing.sm,
    fontSize: 14,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
    textAlign: 'right',
    minHeight: 80,
  },
  dateTimeRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  conditionsContainer: {
    gap: theme.spacing.sm,
  },
  conditionsTitle: {
    fontSize: 16,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  conditionRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  photoSection: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  conditionItem: {
    flex: 1,
  },
  conditionLabel: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    textAlign: 'right',
    marginBottom: theme.spacing.xs,
    fontWeight: '500' as any,
  },
  conditionOptions: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'center',
  },
  conditionOption: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 4,
    backgroundColor: theme.colors.white,
  },
  selectedCondition: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  conditionOptionText: {
    fontSize: 10,
    color: theme.colors.textPrimary,
  },
  selectedConditionText: {
    color: theme.colors.white,
  },
  receivingDetailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  pledgeSection: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: 8,
  },
  pledgeTitle: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  pledgeContent: {
    gap: theme.spacing.sm,
  },
  pledgeText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    textAlign: 'right',
    lineHeight: 20,
  },
  pledgeTextEn: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'left',
    lineHeight: 16,
    marginBottom: theme.spacing.sm,
  },
  signatureSection: {
    margin: theme.spacing.md,
  },
  signatureRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
    justifyContent: 'space-between',
  },
  signatureWrapper: {
    flex: 1,
    minWidth: 280,
  },
  signatureCard: {
    width: '100%',
  },
  signatureHeader: {
    backgroundColor: theme.colors.primary,
    padding: theme.spacing.md,
    borderRadius: 8,
    marginBottom: theme.spacing.md,
  },
  signatureTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.white,
    textAlign: 'center',
  },
  signatureContent: {
    gap: theme.spacing.md,
  },
  signatureBox: {
    height: 60,
    borderWidth: 2,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    backgroundColor: theme.colors.gray50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signaturePlaceholder: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  buttonSection: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    margin: theme.spacing.md,
    justifyContent: 'center',
  },
  submitButton: {
    flex: 1,
    maxWidth: 200,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  printButton: {
    flex: 1,
    maxWidth: 150,
  },
  cancelButton: {
    flex: 1,
    maxWidth: 150,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    shadowColor: theme.colors.gray900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  editButtonText: {
    color: theme.colors.white,
    fontWeight: '600',
    fontSize: 14,
    marginLeft: theme.spacing.xs,
  },
  readOnlyInput: {
    backgroundColor: theme.colors.gray100,
    color: theme.colors.gray600,
  },
  disabledButton: {
    opacity: 0.5,
    backgroundColor: theme.colors.gray300,
  },
  vehicleInfo: {
    fontSize: 14,
    color: theme.colors.primary,
    fontWeight: '500',
    marginTop: theme.spacing.xs,
  },
  // Partner Driver Styles
  partnerDriverHeader: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  partnerIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary + '20',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: 20,
    gap: theme.spacing.sm,
  },
  partnerIndicatorText: {
    fontSize: 16,
    color: theme.colors.primary,
    fontWeight: '600' as any,
    textAlign: 'center',
  },
  partnerInfoBox: {
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.md,
    borderRadius: 8,
    marginBottom: theme.spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.primary,
  },
  partnerInfoTitle: {
    fontSize: 16,
    fontWeight: '600' as any,
    color: theme.colors.primary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  partnerInfoText: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    lineHeight: 16,
    marginBottom: theme.spacing.xs,
    textAlign: 'right',
  },

  // Success Screen Styles
  successScreen: {
    flex: 1,
    backgroundColor: theme.colors.background,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  successContent: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    alignItems: 'center',
    maxWidth: 400,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  successIconContainer: {
    marginBottom: theme.spacing.lg,
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: theme.colors.success,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successIconText: {
    fontSize: 40,
    color: theme.colors.white,
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.xs,
  },
  successTitleEn: {
    fontSize: 20,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  successMessage: {
    fontSize: 16,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.xs,
  },
  successMessageEn: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  successDetails: {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg,
    width: '100%',
  },
  successDetailText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
    textAlign: 'center',
  },
  successButtons: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    width: '100%',
  },
  successButton: {
    flex: 1,
  },
});